import { useRef, useState } from "react";
import { Alert, Button, Chip, Divider, Stack, TextField, Typography } from "@mui/material";
import {
  useAuth,
  useAcceptCoachingProposal,
  useCoachingProposal,
  useCoachingProposals,
  useDecideCoachingProposal,
} from "@bw/shared/hooks";
import type {
  CoachingProposalAcceptanceInput,
  CoachingProposalDecisionInput,
} from "@bw/shared/api";
import { coachingCancellationSummary, formatMoney } from "@bw/shared/utils";
import { useNavigate } from "react-router-dom";

const labels = {
  accept: "Accept proposal",
  decline: "Decline",
  request_revision: "Request changes",
  withdraw: "Withdraw proposal",
};
const refundLabel = (basisPoints: number) => {
  const percent = basisPoints / 100;
  return percent === 100 ? "full refund" : percent === 0 ? "no refund"
    : `${new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(percent)}% refund`;
};
export function coachingTime(value: string, timezone: string) {
  return new Date(value).toLocaleString("en-US", {
    timeZone: timezone,
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export function CoachingProposalPanel(props: {
  requestId: string;
  revisionId: string;
}) {
  const { user, loading } = useAuth();
  if (loading || !user)
    return <Typography role="status">Loading proposal…</Typography>;
  return (
    <ScopedProposalPanel
      key={`${user.uid}:${props.requestId}:${props.revisionId}`}
      {...props}
    />
  );
}

function ScopedProposalPanel({
  requestId,
  revisionId,
}: {
  requestId: string;
  revisionId: string;
}) {
  const [selection, setSelection] = useState<{
    requestId: string;
    revisionId: string;
  } | null>(null);
  const selectedRevisionId =
    selection?.requestId === requestId ? selection.revisionId : revisionId;
  const query = useCoachingProposal(requestId, selectedRevisionId);
  const history = useCoachingProposals(requestId);
  const accept = useAcceptCoachingProposal();
  const decide = useDecideCoachingProposal();
  const keys = useRef(new Map<string, string>());
  const busy = useRef(false);
  const navigate = useNavigate();
  if (query.isError)
    return (
      <Alert severity="error">
        This proposal is unavailable. Refresh to check your access.
      </Alert>
    );
  if (query.fetchStatus === "paused")
    return (
      <Alert severity="info" role="status">
        Waiting for a connection to refresh your proposal.
      </Alert>
    );
  if (!query.data || !query.isCurrent)
    return <Typography role="status">Loading proposal…</Typography>;
  const proposal = query.data;
  if (
    proposal.request_id.toLowerCase() !== requestId.toLowerCase() ||
    proposal.revision_id.toLowerCase() !== selectedRevisionId.toLowerCase()
  ) {
    return <Alert severity="error">This proposal is unavailable.</Alert>;
  }
  const terms = proposal.terms;
  const schedule = terms.schedule;
  const sources =
    history.data?.filter(
      (item) =>
        item.request_id === requestId &&
        item.acceptance_id === proposal.replaces_acceptance_id,
    ) ?? [];
  const original =
    proposal.replaces_acceptance_id &&
    !history.isError &&
    history.fetchStatus === "idle" &&
    history.isFetchedAfterMount &&
    sources.length === 1
      ? sources[0]
      : null;
  const originalUnavailable =
    Boolean(proposal.replaces_acceptance_id) && !original;
  async function act(action: keyof typeof labels) {
    if (
      !query.canAct() ||
      busy.current ||
      query.fetchStatus !== "idle" ||
      (action === "accept" && originalUnavailable) ||
      !proposal.available_actions.includes(action)
    )
      return;
    busy.current = true;
    const key = `${selectedRevisionId}:${action}`;
    const idempotency_key = keys.current.get(key) ?? crypto.randomUUID();
    keys.current.set(key, idempotency_key);
    const body: CoachingProposalAcceptanceInput = {
      revision_id: selectedRevisionId,
      expected_revision: proposal.revision,
      idempotency_key,
    };
    try {
      if (action === "accept") {
        const accepted = await accept.mutateAsync([requestId, body]);
        const tab = proposal.viewer_roles.includes("payer") ? "payments" : "lessons";
        navigate(`/me/lessons?tab=${tab}&acceptance_id=${encodeURIComponent(accepted.acceptance_id)}`);
      }
      else
        await decide.mutateAsync([
          requestId,
          { ...body, action, note: null } as CoachingProposalDecisionInput,
        ]);
    } catch {
      /* The mutation error below remains visible. */
    } finally {
      busy.current = false;
    }
  }
  return (
    <Stack
      component="section"
      aria-label="Proposal"
      spacing={1.5}
      sx={{ mt: 2 }}
    >
      {history.data?.length ? (
        <TextField
          select
          label="Proposal revision"
          size="small"
          value={selectedRevisionId}
          slotProps={{ select: { native: true } }}
          onChange={(event) =>
            setSelection({ requestId, revisionId: event.target.value })
          }
        >
          {history.data.map((item) => (
            <option key={item.revision_id} value={item.revision_id}>
              Revision {item.revision} — {item.state.replaceAll("_", " ")}
            </option>
          ))}
        </TextField>
      ) : null}
      {history.isError ? (
        <Alert severity="warning">Proposal history is unavailable.</Alert>
      ) : null}
      {proposal.replaces_acceptance_id ? (
        <>
          <Alert severity="info">
            Reschedule proposal: completed lessons stay in your history, and
            this change requires no additional payment.
          </Alert>
          {original ? (
            <Button
              size="small"
              sx={{ alignSelf: "flex-start" }}
              disabled={
                query.fetchStatus !== "idle" ||
                accept.isPending ||
                decide.isPending
              }
              onClick={() =>
                setSelection({ requestId, revisionId: original.revision_id })
              }
            >
              Review original agreement · Revision {original.revision}
            </Button>
          ) : (
            <Alert severity="warning">
              The original agreement is unavailable. Refresh history to review
              the original agreement.
              <Button
                size="small"
                disabled={history.fetchStatus !== "idle"}
                onClick={() => void history.refetch()}
              >
                Refresh history
              </Button>
            </Alert>
          )}
        </>
      ) : null}
      <Typography sx={{ fontWeight: 700 }}>
        {terms.service_name} · {formatMoney(terms.total_amount, terms.currency)}
      </Typography>
      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
        <Chip size="small" color="primary" variant="outlined" label="One payment" />
        <Chip size="small" variant="outlined" label={schedule.kind === "fixed"
          ? `${schedule.sessions.length} ${schedule.sessions.length === 1 ? "session" : "sessions"}`
          : `${schedule.credits.length} sessions`} />
      </Stack>
      <Typography variant="body2">
        {proposal.state.replaceAll("_", " ")} · Revision {proposal.revision}
      </Typography>
      <Typography variant="body2">Times in {schedule.timezone}</Typography>
      {schedule.kind === "fixed" ? (
        schedule.sessions.map((session) => (
          <Typography key={session.session_key} variant="body2">
            {coachingTime(session.starts_at, schedule.timezone)} –{" "}
            {coachingTime(session.ends_at, schedule.timezone)}
            {" · "}
            {session.court_ids.length
              ? session.court_ids
                  .map(
                    (id) =>
                      proposal.courts.find((court) => court.court_id === id)
                        ?.name ?? "Court unavailable",
                  )
                  .join(", ")
              : "No court required"}
          </Typography>
        ))
      ) : (
        <>
          <Typography variant="body2">
            {schedule.credits.length} sessions ·{" "}
            {schedule.session_duration_minutes} minutes each ·{" "}
            {schedule.required_court_count} courts per session
          </Typography>
          {schedule.allowed_court_ids.length ? (
            <Typography variant="body2">
              Eligible courts:{" "}
              {proposal.courts.map((court) => court.name).join(", ")}
            </Typography>
          ) : null}
          <Typography variant="body2">
            Book from{" "}
            {coachingTime(schedule.booking_opens_at, schedule.timezone)} until{" "}
            {coachingTime(schedule.booking_closes_at, schedule.timezone)}.
          </Typography>
          <Typography variant="body2">
            Complete sessions by{" "}
            {coachingTime(schedule.sessions_complete_by, schedule.timezone)}.
          </Typography>
        </>
      )}
      <Typography variant="body2">
        Accept by {coachingTime(terms.expires_at, schedule.timezone)}. Payment
        due {coachingTime(terms.payment_due_at, schedule.timezone)}.
      </Typography>
      <Typography component="h3" variant="subtitle2">
        Cancellation policy
      </Typography>
      {coachingCancellationSummary(terms.cancellation).map((line) => (
        <Typography key={line} variant="body2">
          {line}
        </Typography>
      ))}
      <Typography variant="body2">
        No-show: {refundLabel(terms.cancellation.no_show_refund_basis_points)}. {" "}
        {terms.cancellation.no_show_consumes_credit
          ? "Missing a session uses that session’s credit."
          : "Missing a session does not use its credit."}
      </Typography>
      {schedule.kind === "flexible_package" ? (
        <Typography variant="body2">
          Unused package credits: {refundLabel(terms.cancellation.unused_package_refund_basis_points)}.
        </Typography>
      ) : null}
      {accept.isError || decide.isError ? (
        <Alert severity="error">
          The proposal could not be updated. Check the latest terms and try
          again.
        </Alert>
      ) : null}
      <Divider />
      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
        {proposal.available_actions.map((action) => (
          <Button
            key={action}
            size="small"
            variant={action === "accept" ? "contained" : "outlined"}
            disabled={
              query.fetchStatus !== "idle" ||
              accept.isPending ||
              decide.isPending ||
              (action === "accept" && originalUnavailable)
            }
            onClick={() => void act(action)}
          >
            {labels[action]}
          </Button>
        ))}
      </Stack>
    </Stack>
  );
}
